import type { FormQuestion } from "../../../data/live";

export interface Attendee {
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  phone: string;
  tierId: string;
  seat: string;
  spotId: string;
  holdId: string;
  useCredit: boolean;
  memberCreditProductId: string;
  memberCreditPunchCardId?: string;
  /** Owned free membership access; never interpreted as a spendable credit. */
  memberAccessProductId: string;
  familyUserId: number;
  familyRosterId: number;
}

export function emptyAttendee(): Attendee {
  return {
    firstName: "",
    lastName: "",
    dob: "",
    email: "",
    phone: "",
    tierId: "dropin",
    seat: "",
    spotId: "",
    holdId: "",
    useCredit: false,
    memberCreditProductId: "",
    memberAccessProductId: "",
    familyUserId: 0,
    familyRosterId: 0,
  };
}

export const BOOKING_STEPS = [
  "Your details",
  "Pick your spots",
  "Choose how to pay",
  "Payment",
] as const;

export const MAX_EXTRA_ATTENDEES = 8;

export const RECIPIENT_CHOICES: readonly {
  value: "family" | "guest";
  title: string;
  sub: string;
}[] = [
  { value: "family", title: "Family member", sub: "Someone on your account" },
  { value: "guest", title: "Guest", sub: "Enter their details manually" },
];

export const DEFAULT_WAIVER_PARAGRAPHS = [
  "I, the undersigned, acknowledge that participation in the activities offered by the studio carries inherent risks of physical injury. I confirm that I am physically able to participate and have disclosed any conditions that may affect my safe practice.",
  "I voluntarily assume all risk of injury, loss, or damage to person or property arising from participation, including (but not limited to) strains, sprains, falls, and contact with equipment. I agree to follow all instructor and studio guidance.",
  "I release the studio, its instructors, employees, and contractors from any and all claims, demands, and causes of action arising out of my participation, except where caused by gross negligence.",
  "This waiver is binding upon me, my heirs, and my legal representatives. I have read this waiver in full and understand its terms.",
];

export type RichFormQuestion = FormQuestion & { detailsHtml?: string };

const HTML_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  mdash: "—",
  ndash: "–",
  hellip: "…",
};

function decodeEntities(source: string): string {
  return source.replace(
    /&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g,
    (match, body: string) => {
      if (body[0] !== "#") return HTML_ENTITIES[body.toLowerCase()] ?? match;
      const code =
        body[1] === "x" || body[1] === "X"
          ? parseInt(body.slice(2), 16)
          : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : match;
    },
  );
}

/**
 * Flatten org-authored rich text into readable paragraphs without allowing
 * authored markup into the checkout template.
 */
export function toParagraphs(source?: string): string[] {
  if (!source) return [];
  const flattened = source
    .replace(/<\s*(br|hr)\s*\/?\s*>/gi, "\n")
    .replace(
      /<\s*\/\s*(p|div|h[1-6]|li|tr|blockquote|section|article)\s*>/gi,
      "\n",
    )
    .replace(/<[^>]*>/g, " ");
  return decodeEntities(flattened)
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}
